import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLANT_OPTS = [
  // Nilkamal Ltd. plants
  'Sinnar (Nashik) Plant', 'Vasona (Maharashtra) Plant', 'Jammu Plant',
  'Hosur (Tamil Nadu) Plant', 'Puducherry Plant', 'Hyderabad Plant',
  'Barjora (West Bengal) Plant', 'Mahad Plant', 'Noida Plant',
  // Warehouses / depots
  'Mumbai HO', 'Mumbai Warehouse', 'Delhi Warehouse', 'Kolkata Warehouse',
  'Chennai Warehouse', 'Bangalore Warehouse', 'Ahmedabad Warehouse',
  'Pune Warehouse', 'Hyderabad Warehouse',
  'Other',
];
const DEPT_OPTS = [
  'Production', 'Quality', 'Service', 'Design', 'New Product Development (NPD)',
  'Purchase', 'Sales', 'Marketing', 'HR', 'Legal',
  'Architect Dept', 'Administrative Dept', 'Dispatch', 'Stores', 'Assembly',
];
const SHIFT_OPTS = ['Shift 1', 'Shift 2', 'Shift 3', 'General'];
const DELIVERY_DELAY_OPTS = ['No Delay', '< 1 Hour', '1–4 Hours', '4–8 Hours', '1 Day', '2–3 Days', '1 Week', 'More than 1 Week'];
const RCA_METHODS = ['5 Whys', 'Supervisor Review', 'Cross Functional Review'];
const RC_CATEGORIES = ['Man', 'Machine', 'Method', 'Material', 'Measurement', 'Environment'];
const RC_CAT_HINTS = {
  'Man': 'Skill / negligence / fatigue',
  'Machine': 'Breakdown / calibration',
  'Method': 'SOP / workflow issue',
  'Material': 'Input quality',
  'Measurement': 'No control system',
  'Environment': 'Layout / conditions',
};
const RESPONSIBILITY_LEVELS = ['operator', 'supervisor', 'system'];
const CLASSIFICATIONS = [
  { value: 'Level 0 – Process Failure (No Personal Memo)', short: 'Level 0', desc: 'System / SOP / training gap. No personal blame.', action: 'Fix the system. Coaching only, no memo on file.', color: '#10B981' },
  { value: 'Level 1 – Honest Mistake / Slip', short: 'Level 1', desc: 'Trained employee, momentary lapse despite doing the right things.', action: 'Coaching + Memo (for record)', color: '#3B82F6' },
  { value: 'Level 2 – At-Risk Behaviour', short: 'Level 2', desc: 'Took a shortcut believing it was justified. Not malicious.', action: 'Counselling + Warning Memo', color: '#F59E0B' },
  { value: 'Level 3 – Recklessness / Negligence', short: 'Level 3', desc: 'Knew the rule, deliberately ignored consequences.', action: 'Final Warning + Penalty + Appraisal Impact', color: '#EF4444' },
  { value: 'Level 4 – Wilful Violation / Misconduct', short: 'Level 4', desc: 'Deliberate breach, falsification, gross negligence, or repeat offender.', action: 'HR Escalation / Termination Recommendation', color: '#991B1B' },
];
const ACTION_TYPES = ['Coaching', 'Memo', 'Warning Memo', 'Corrective Action Notice', 'HR Escalation', 'Appraisal Impact'];

// Sub-fields per action type. type: 'text'|'date'|'textarea'|'select'|'number'
const ACTION_FIELDS = {
  'Coaching': [
    { key: 'coached_by', label: 'Coached By', type: 'text', placeholder: 'Coach / Mentor name' },
    { key: 'coaching_date', label: 'Coaching Date', type: 'date' },
    { key: 'topic_skills', label: 'Topic / Skills to Cover', type: 'textarea' },
    { key: 'follow_up_date', label: 'Follow-up Date', type: 'date' },
  ],
  'Memo': [
    { key: 'issued_by', label: 'Issued By', type: 'text' },
    { key: 'issued_on', label: 'Issued On', type: 'date' },
    { key: 'validity', label: 'Validity', type: 'select', options: ['3 Months', '6 Months', '1 Year', 'Permanent Record'] },
    { key: 'notes', label: 'Notes', type: 'textarea' },
  ],
  'Warning Memo': [
    { key: 'warning_level', label: 'Warning Level', type: 'select', options: ['Verbal', 'Written', 'Final Written'] },
    { key: 'issued_on', label: 'Issued On', type: 'date' },
    { key: 'valid_until', label: 'Valid Until', type: 'date' },
    { key: 'past_warnings_count', label: 'Past Warnings Count', type: 'number' },
  ],
  'Corrective Action Notice': [
    { key: 'can_number', label: 'CAN Number', type: 'text' },
    { key: 'owner', label: 'Action Owner', type: 'text' },
    { key: 'due_date', label: 'Due Date', type: 'date' },
    { key: 'action_required', label: 'Action Required', type: 'textarea' },
  ],
  'HR Escalation': [
    { key: 'escalated_to', label: 'Escalated To (HR rep)', type: 'text' },
    { key: 'escalation_date', label: 'Escalation Date', type: 'date' },
    { key: 'mode', label: 'Mode', type: 'select', options: ['Email', 'Letter', 'In-person Meeting', 'Phone Call'] },
    { key: 'expected_outcome', label: 'Expected Outcome', type: 'textarea' },
  ],
  'Appraisal Impact': [
    { key: 'impact_type', label: 'Impact Type', type: 'select', options: ['Rating downgrade', 'Increment hold', 'Bonus reduction', 'Promotion hold', 'Other'] },
    { key: 'effective_cycle', label: 'Effective Cycle', type: 'text', placeholder: 'e.g. FY 2026-27' },
    { key: 'magnitude', label: 'Magnitude / Description', type: 'textarea' },
  ],
};

const emptyRCA = {
  step1_incident_clarity: {
    incident_time: '',
    incident_location: '',
    incident_sequence: '',
    logs_available: '',           // 'Yes' | 'No' | ''
    logs_notes: '',
    repeat_issue_confirmed: '',   // 'Yes' | 'No' | ''
  },
  step2_system_check: {
    sop_exists: '', sop_clear: '', employee_trained: '',
    checklist_available: '', supervision_present: '',
  },
  five_whys: ['', '', '', '', ''],
  step5_responsibility: {
    operator_could_prevent: '', supervisor_could_prevent: '', system_could_prevent: '',
    assigned_responsibility_level: '',
  },
  step6_fairness: {
    being_blamed_for_system_failure: '',
    another_person_would_also_fail: '',
  },
};

const empty = {
  date_of_incident: '', time_of_incident: '', plant_location: 'Sinnar (Nashik) Plant', plant_location_other: '',
  department: 'Production', shift: 'Shift 1', reported_by: '',
  employee_name: '', employee_code: '', designation: '', reporting_manager: '', employee_user_id: '',
  incident_description: '', exact_location_of_incident: '', repeat_issue: 'No',
  production_loss_units: '', estimated_financial_impact: '', quality_impact: 'No', safety_impact: 'No', delivery_delay: 'No Delay',
  rca_method: '5 Whys', root_cause_category: 'Man', root_cause_statement: '',
  rca_checklist: emptyRCA,
  accountable_person: '', reason_for_accountability: '',
  immediate_corrective_action: '', immediate_action_owner: '', immediate_action_date: '', preventive_action: '', preventive_action_owner: '', target_closure_date: '',
  incident_classification: 'Level 1 – Honest Mistake / Slip',
  action_types: [], action_details: {}, additional_remarks: '', penalty_amount: '',
  supervisor_name: '', plant_head_approval: '', hr_representative: '',
};

const SECTIONS = [
  { id: 1, title: '1. Incident Information' },
  { id: 2, title: '2. Employee Details' },
  { id: 3, title: '3. Incident Description' },
  { id: 4, title: '4. Impact Assessment' },
  { id: 5, title: '5. Root Cause Analysis' },
  { id: 6, title: '6. Accountability' },
  { id: 7, title: '7. Actions' },
  { id: 8, title: '8. Classification' },
  { id: 9, title: '9. Action Taken' },
  { id: 10, title: '10. Approvals' },
];

const Field = ({ label, required, children, hint }) => (
  <div>
    <label className="block text-xs font-medium text-[#3B5A54] mb-1">{label}{required && <span className="text-red-500"> *</span>}</label>
    {children}
    {hint && <p className="text-[10px] text-[#6D8E87] mt-0.5">{hint}</p>}
  </div>
);

const inputCls = 'w-full px-3 py-2 border border-[#D5E8E4] rounded-lg outline-none focus:ring-2 focus:ring-[#00bfb3] text-sm bg-white';

// Small Yes/No pill picker
const YesNo = ({ value, onChange, testId }) => (
  <div className="flex gap-1.5 shrink-0">
    {['Yes', 'No'].map(v => (
      <button type="button" key={v} onClick={() => onChange(v)}
        className={`px-3 py-1 text-xs font-semibold rounded-full border transition ${value === v ? (v === 'Yes' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-red-500 text-white border-red-500') : 'bg-white text-[#3B5A54] border-[#D5E8E4]'}`}
        data-testid={`${testId}-${v.toLowerCase()}`}>{v}</button>
    ))}
  </div>
);

const MemoModal = ({ open, onClose, existing, onSaved }) => {
  const [form, setForm] = useState(existing ? { ...empty, ...existing } : empty);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);

  useEffect(() => {
    // Load assignable users (so employee_user_id picker is populated)
    axios.get(`${API}/goals/assignable-users`).then(r => setUsers(r.data.users || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setActionDetail = (action, field, value) => {
    setForm(f => {
      const next = { ...f, action_details: { ...(f.action_details || {}) } };
      next.action_details[action] = { ...(next.action_details[action] || {}), [field]: value };
      return next;
    });
  };
  const toggleAction = (a) => set('action_types', form.action_types.includes(a) ? form.action_types.filter(x => x !== a) : [...form.action_types, a]);

  // Helpers for nested RCA state
  const setRca = (path, value) => {
    setForm(f => {
      const next = { ...f, rca_checklist: { ...(f.rca_checklist || emptyRCA) } };
      const parts = path.split('.');
      let cur = next.rca_checklist;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };
  const rca = form.rca_checklist || emptyRCA;
  const sysCheckAnyNo = ['sop_exists','sop_clear','employee_trained','checklist_available','supervision_present']
    .some(k => rca.step2_system_check?.[k] === 'No');
  const fairnessFlag = rca.step6_fairness?.being_blamed_for_system_failure === 'Yes' || rca.step6_fairness?.another_person_would_also_fail === 'Yes';

  // When entering step 3, prefill exact_location_of_incident from plant if empty
  useEffect(() => {
    if (step === 3 && !form.exact_location_of_incident && form.plant_location) {
      const loc = form.plant_location === 'Other' ? (form.plant_location_other || '') : form.plant_location;
      if (loc) set('exact_location_of_incident', loc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Auto-suggest classification when entering Step 8 (only if user hasn't manually customised)
  const classManuallyChosenRef = useRef(!!existing);
  const computeSuggestion = () => {
    const s2 = rca.step2_system_check || {};
    const s5 = rca.step5_responsibility || {};
    const s6 = rca.step6_fairness || {};
    const sysFailed = ['sop_exists','sop_clear','employee_trained','checklist_available','supervision_present'].some(k => s2[k] === 'No');
    const fairnessFlagged = s6.being_blamed_for_system_failure === 'Yes' || s6.another_person_would_also_fail === 'Yes';
    if (sysFailed || fairnessFlagged) {
      const reasons = [];
      if (sysFailed) reasons.push('Step 2 has a missing safeguard');
      if (fairnessFlagged) reasons.push('Step 6 fairness check flagged');
      return { value: 'Level 0 – Process Failure (No Personal Memo)', reasons };
    }
    const operatorCould = s5.operator_could_prevent === 'Yes';
    const repeat = (rca.step1_incident_clarity || {}).repeat_issue_confirmed === 'Yes' || form.repeat_issue === 'Yes';
    if (operatorCould && repeat) {
      return { value: 'Level 3 – Recklessness / Negligence', reasons: ['Operator could have prevented and it is a repeat issue'] };
    }
    if (operatorCould) {
      return { value: 'Level 2 – At-Risk Behaviour', reasons: ['Operator could have prevented the incident'] };
    }
    return { value: 'Level 1 – Honest Mistake / Slip', reasons: ['No system failure, no operator preventability'] };
  };
  const [suggestion, setSuggestion] = useState(null);
  useEffect(() => {
    if (step === 8) {
      const sg = computeSuggestion();
      setSuggestion(sg);
      if (!classManuallyChosenRef.current) set('incident_classification', sg.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const submit = async () => {
    setError('');
    setSaving(true);
    try {
      const payload = {
        ...form,
        production_loss_units: parseFloat(form.production_loss_units) || 0,
        estimated_financial_impact: parseFloat(form.estimated_financial_impact) || 0,
        penalty_amount: parseFloat(form.penalty_amount) || 0,
      };
      if (existing) await axios.put(`${API}/memos/${existing.id}`, payload);
      else await axios.post(`${API}/memos`, payload);
      onSaved?.();
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : (Array.isArray(d) ? d.map(x => x.msg).join('; ') : 'Failed to save memo'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="memo-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
            {existing ? 'Edit Memo' : 'New Memo'}
          </DialogTitle>
          <p className="text-xs text-[#6D8E87]">Employee Accountability &amp; Incident Reporting Form</p>
        </DialogHeader>

        {/* Step pills */}
        <div className="flex flex-wrap gap-1.5 mb-4 sticky top-0 bg-white z-10 py-2">
          {SECTIONS.map(s => (
            <button key={s.id} type="button" onClick={() => setStep(s.id)}
              className={`px-2.5 py-1 text-[11px] rounded-full font-medium transition ${step === s.id ? 'bg-[#00a69c] text-white' : 'bg-[#F4F9F7] text-[#3B5A54] hover:bg-[#E2F3F0]'}`}
              data-testid={`memo-step-${s.id}`}>
              {s.title}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {step === 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Date of Incident" required><input type="date" value={form.date_of_incident} onChange={e=>set('date_of_incident',e.target.value)} className={inputCls} data-testid="f-date"/></Field>
              <Field label="Time of Incident" required><input type="time" value={form.time_of_incident} onChange={e=>set('time_of_incident',e.target.value)} className={inputCls} data-testid="f-time"/></Field>
              <Field label="Plant / Location" required><select value={form.plant_location} onChange={e=>set('plant_location',e.target.value)} className={inputCls} data-testid="f-plant">{PLANT_OPTS.map(o=><option key={o}>{o}</option>)}</select></Field>
              {form.plant_location === 'Other' && <Field label="Specify location" required><input value={form.plant_location_other} onChange={e=>set('plant_location_other',e.target.value)} className={inputCls} data-testid="f-plant-other"/></Field>}
              <Field label="Department" required><select value={form.department} onChange={e=>set('department',e.target.value)} className={inputCls} data-testid="f-dept">{DEPT_OPTS.map(o=><option key={o}>{o}</option>)}</select></Field>
              <Field label="Shift" required><select value={form.shift} onChange={e=>set('shift',e.target.value)} className={inputCls} data-testid="f-shift">{SHIFT_OPTS.map(o=><option key={o}>{o}</option>)}</select></Field>
              <Field label="Reported By" required><input value={form.reported_by} onChange={e=>set('reported_by',e.target.value)} className={inputCls} data-testid="f-reported"/></Field>
            </div>
          )}

          {step === 2 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Employee Name" required><input value={form.employee_name} onChange={e=>set('employee_name',e.target.value)} className={inputCls} data-testid="f-emp-name"/></Field>
              <Field label="Employee Code" required><input value={form.employee_code} onChange={e=>set('employee_code',e.target.value)} className={inputCls} data-testid="f-emp-code"/></Field>
              <Field label="Designation" required><input value={form.designation} onChange={e=>set('designation',e.target.value)} className={inputCls} data-testid="f-desig"/></Field>
              <Field label="Reporting Manager" required><input value={form.reporting_manager} onChange={e=>set('reporting_manager',e.target.value)} className={inputCls} data-testid="f-rep-mgr"/></Field>
              <Field label="Link to Vooxy user (optional)" hint="If linked, the employee will receive a notification and can acknowledge in their Inbox">
                <select value={form.employee_user_id} onChange={e=>{
                  const u = users.find(x => x.id === e.target.value);
                  set('employee_user_id', e.target.value);
                  if (u) { set('employee_name', u.name); set('designation', u.role); }
                }} className={inputCls} data-testid="f-emp-user">
                  <option value="">— Not linked —</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role}{u.region?`, ${u.region}`:''})</option>)}
                </select>
              </Field>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <Field label="Incident Description (facts only, no opinions)" required><textarea rows={4} value={form.incident_description} onChange={e=>set('incident_description',e.target.value)} className={inputCls} data-testid="f-desc"/></Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Exact Location of Incident" required><input value={form.exact_location_of_incident} onChange={e=>set('exact_location_of_incident',e.target.value)} className={inputCls} data-testid="f-loc"/></Field>
                <Field label="Was this a Repeat Issue?" required>
                  <div className="flex gap-2">
                    {['Yes','No'].map(v=><button type="button" key={v} onClick={()=>set('repeat_issue',v)} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${form.repeat_issue===v?'bg-[#00a69c] text-white border-[#00a69c]':'bg-white text-[#3B5A54] border-[#D5E8E4]'}`} data-testid={`f-repeat-${v.toLowerCase()}`}>{v}</button>)}
                  </div>
                </Field>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Production Loss (Units)" required><input type="number" min="0" value={form.production_loss_units} onChange={e=>set('production_loss_units',e.target.value)} className={inputCls} data-testid="f-prod-loss"/></Field>
              <Field label="Estimated Financial Impact (₹)" required><input type="number" min="0" value={form.estimated_financial_impact} onChange={e=>set('estimated_financial_impact',e.target.value)} className={inputCls} data-testid="f-fin-impact"/></Field>
              <Field label="Quality Impact" required>
                <div className="flex gap-2">{['Yes','No'].map(v=><button type="button" key={v} onClick={()=>set('quality_impact',v)} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${form.quality_impact===v?'bg-[#00a69c] text-white border-[#00a69c]':'bg-white text-[#3B5A54] border-[#D5E8E4]'}`}>{v}</button>)}</div>
              </Field>
              <Field label="Safety Impact" required>
                <div className="flex gap-2">{['Yes','No'].map(v=><button type="button" key={v} onClick={()=>set('safety_impact',v)} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${form.safety_impact===v?'bg-[#00a69c] text-white border-[#00a69c]':'bg-white text-[#3B5A54] border-[#D5E8E4]'}`}>{v}</button>)}</div>
              </Field>
              <Field label="Delivery Delay" required>
                <select value={form.delivery_delay} onChange={e=>set('delivery_delay',e.target.value)} className={inputCls} data-testid="f-delay">
                  {DELIVERY_DELAY_OPTS.map(o=><option key={o}>{o}</option>)}
                </select>
              </Field>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4">
              <div className="p-3 bg-[#F4F9F7] rounded-lg text-xs text-[#3B5A54]" data-testid="rca-intro">
                <strong className="text-[#052A23]">RCA Quick Checklist.</strong> Work through each step in order. The system will flag if this looks like a process failure (no memo needed) vs a personal accountability incident.
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="RCA Method Used" required><select value={form.rca_method} onChange={e=>set('rca_method',e.target.value)} className={inputCls} data-testid="f-rca">{RCA_METHODS.map(o=><option key={o}>{o}</option>)}</select></Field>
                <Field label="Root Cause Category" required>
                  <select value={form.root_cause_category} onChange={e=>set('root_cause_category',e.target.value)} className={inputCls} data-testid="f-rcc">
                    {RC_CATEGORIES.map(o=><option key={o}>{o} — {RC_CAT_HINTS[o]}</option>)}
                  </select>
                </Field>
              </div>

              {/* STEP 1 */}
              <div className="border border-[#D5E8E4] rounded-xl p-3" data-testid="rca-step1">
                <div className="font-semibold text-[#052A23] text-sm mb-2">Step 1 — Incident Clarity</div>
                <div className="text-xs font-medium text-[#3B5A54] mb-1">Exact time, location and sequence documented:</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <Field label="Exact Time"><input type="time" value={rca.step1_incident_clarity?.incident_time || ''} onChange={e=>setRca('step1_incident_clarity.incident_time', e.target.value)} className={inputCls} data-testid="rca-s1-time"/></Field>
                  <Field label="Exact Location"><input type="text" value={rca.step1_incident_clarity?.incident_location || ''} onChange={e=>setRca('step1_incident_clarity.incident_location', e.target.value)} className={inputCls} placeholder="e.g. Assembly line 3, station 7" data-testid="rca-s1-location"/></Field>
                </div>
                <Field label="Sequence of Events"><textarea rows={2} value={rca.step1_incident_clarity?.incident_sequence || ''} onChange={e=>setRca('step1_incident_clarity.incident_sequence', e.target.value)} className={inputCls} placeholder="Step-by-step what happened, in order" data-testid="rca-s1-sequence"/></Field>

                <div className="mt-3 p-2.5 bg-[#F4F9F7] rounded-lg">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <span className="text-sm text-[#3B5A54]">Logs / reports / machine data available?</span>
                    <YesNo value={rca.step1_incident_clarity?.logs_available} onChange={v=>setRca('step1_incident_clarity.logs_available', v)} testId="rca-s1-logs"/>
                  </div>
                  {rca.step1_incident_clarity?.logs_available === 'Yes' && (
                    <textarea rows={2} value={rca.step1_incident_clarity?.logs_notes || ''} onChange={e=>setRca('step1_incident_clarity.logs_notes', e.target.value)} className={inputCls} placeholder="Describe what data is available (file names, system, reference IDs)" data-testid="rca-s1-logs-notes"/>
                  )}
                </div>

                <div className="mt-3 flex items-center justify-between gap-3">
                  <span className="text-sm text-[#3B5A54]">Is this a repeat issue?</span>
                  <YesNo value={rca.step1_incident_clarity?.repeat_issue_confirmed} onChange={v=>setRca('step1_incident_clarity.repeat_issue_confirmed', v)} testId="rca-s1-repeat"/>
                </div>
              </div>

              {/* STEP 2 */}
              <div className="border border-[#D5E8E4] rounded-xl p-3" data-testid="rca-step2">
                <div className="font-semibold text-[#052A23] text-sm mb-2">Step 2 — System Check <span className="text-red-500 font-normal text-xs">(critical)</span></div>
                {[
                  ['sop_exists', 'SOP exists?'],
                  ['sop_clear', 'SOP is clear and accessible?'],
                  ['employee_trained', 'Employee trained on SOP?'],
                  ['checklist_available', 'Checklist available?'],
                  ['supervision_present', 'Supervision present?'],
                ].map(([k, label]) => (
                  <div key={k} className="flex items-center justify-between gap-3 py-1.5">
                    <span className="text-sm text-[#3B5A54]">{label}</span>
                    <YesNo value={rca.step2_system_check?.[k]} onChange={v=>setRca(`step2_system_check.${k}`, v)} testId={`rca-s2-${k}`}/>
                  </div>
                ))}
                {sysCheckAnyNo && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-300 rounded-lg text-xs text-amber-900" data-testid="system-failure-hint">
                    ⚠️ <strong>Likely Process Failure.</strong> One or more system safeguards are missing. Consider whether a personal memo is the right outcome — fixing the system may be more effective.
                  </div>
                )}
              </div>

              {/* STEP 3 */}
              <div className="border border-[#D5E8E4] rounded-xl p-3" data-testid="rca-step3">
                <div className="font-semibold text-[#052A23] text-sm mb-2">Step 3 — 5 Why Analysis</div>
                {(rca.five_whys || ['','','','','']).map((w, i) => (
                  <div key={i} className="mb-2">
                    <label className="block text-xs text-[#6D8E87] mb-0.5">Why #{i + 1}</label>
                    <input type="text" value={w || ''}
                      onChange={e => {
                        const next = [...(rca.five_whys || ['','','','',''])];
                        next[i] = e.target.value;
                        setRca('five_whys', next);
                      }}
                      placeholder={i === 0 ? 'Why did it happen?' : 'Why did that happen?'}
                      className={inputCls} data-testid={`rca-why-${i + 1}`}/>
                  </div>
                ))}
                <p className="text-[10px] text-[#6D8E87] mt-1">👉 The final Why is your <strong>root cause</strong> — capture it in the statement below.</p>
              </div>

              <Field label="Root Cause Statement (final why)" required>
                <textarea rows={3} value={form.root_cause_statement} onChange={e=>set('root_cause_statement',e.target.value)} className={inputCls} data-testid="f-rcs"/>
              </Field>

              {/* STEP 5 */}
              <div className="border border-[#D5E8E4] rounded-xl p-3" data-testid="rca-step5">
                <div className="font-semibold text-[#052A23] text-sm mb-2">Step 5 — Responsibility Test</div>
                {[
                  ['operator_could_prevent', 'Could the operator have prevented it?'],
                  ['supervisor_could_prevent', 'Could the supervisor have prevented it?'],
                  ['system_could_prevent', 'Could the system have prevented it?'],
                ].map(([k, label]) => (
                  <div key={k} className="flex items-center justify-between gap-3 py-1.5">
                    <span className="text-sm text-[#3B5A54]">{label}</span>
                    <YesNo value={rca.step5_responsibility?.[k]} onChange={v=>setRca(`step5_responsibility.${k}`, v)} testId={`rca-s5-${k}`}/>
                  </div>
                ))}
                <div className="mt-2">
                  <label className="block text-xs text-[#6D8E87] mb-1">Assigned responsibility level (lowest preventable level):</label>
                  <div className="flex gap-2 flex-wrap">
                    {RESPONSIBILITY_LEVELS.map(lvl => (
                      <button type="button" key={lvl}
                        onClick={()=>setRca('step5_responsibility.assigned_responsibility_level', lvl)}
                        className={`px-3 py-1.5 text-xs rounded-full border ${rca.step5_responsibility?.assigned_responsibility_level === lvl ? 'bg-[#00a69c] text-white border-[#00a69c]' : 'bg-white border-[#D5E8E4] text-[#3B5A54]'}`}
                        data-testid={`rca-s5-level-${lvl}`}>
                        {lvl.charAt(0).toUpperCase() + lvl.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* STEP 6 */}
              <div className="border border-amber-300 bg-amber-50 rounded-xl p-3" data-testid="rca-step6">
                <div className="font-semibold text-amber-900 text-sm mb-2">Step 6 — Fairness Check <span className="font-normal text-xs">(mandatory)</span></div>
                <div className="flex items-center justify-between gap-3 py-1.5">
                  <span className="text-sm text-amber-900">Is this person being blamed for a system failure?</span>
                  <YesNo value={rca.step6_fairness?.being_blamed_for_system_failure} onChange={v=>setRca('step6_fairness.being_blamed_for_system_failure', v)} testId="rca-s6-blame"/>
                </div>
                <div className="flex items-center justify-between gap-3 py-1.5">
                  <span className="text-sm text-amber-900">Would another person in the same role also fail?</span>
                  <YesNo value={rca.step6_fairness?.another_person_would_also_fail} onChange={v=>setRca('step6_fairness.another_person_would_also_fail', v)} testId="rca-s6-anyone"/>
                </div>
                {fairnessFlag && (
                  <div className="mt-2 p-2 bg-white border border-amber-400 rounded-lg text-xs text-amber-900" data-testid="fairness-warning">
                    ⚠️ <strong>Reconsider.</strong> If either answer is Yes, a personal memo may be unfair — focus on system / process correction instead.
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-3">
              <Field label="Accountable Person" required><input value={form.accountable_person} onChange={e=>set('accountable_person',e.target.value)} className={inputCls} data-testid="f-acc-person"/></Field>
              <Field label="Reason for Accountability" required><textarea rows={3} value={form.reason_for_accountability} onChange={e=>set('reason_for_accountability',e.target.value)} className={inputCls} data-testid="f-acc-reason"/></Field>
            </div>
          )}

          {step === 7 && (
            <div className="space-y-3">
              <Field label="Immediate Corrective Action" required><textarea rows={2} value={form.immediate_corrective_action} onChange={e=>set('immediate_corrective_action',e.target.value)} className={inputCls} data-testid="f-imm"/></Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Immediate Action Owner" required><input value={form.immediate_action_owner} onChange={e=>set('immediate_action_owner',e.target.value)} className={inputCls} data-testid="f-imm-owner"/></Field>
                <Field label="Immediate Action Date" required><input type="date" value={form.immediate_action_date} onChange={e=>set('immediate_action_date',e.target.value)} className={inputCls} data-testid="f-imm-date"/></Field>
              </div>
              <Field label="Preventive Action" required><textarea rows={2} value={form.preventive_action} onChange={e=>set('preventive_action',e.target.value)} className={inputCls} data-testid="f-prev"/></Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Preventive Action Owner" required><input value={form.preventive_action_owner} onChange={e=>set('preventive_action_owner',e.target.value)} className={inputCls} data-testid="f-prev-owner"/></Field>
                <Field label="Target Closure Date" required><input type="date" value={form.target_closure_date} onChange={e=>set('target_closure_date',e.target.value)} className={inputCls} data-testid="f-target-date"/></Field>
              </div>
            </div>
          )}

          {step === 8 && (
            <div className="space-y-3">
              {suggestion && (
                <div className="p-3 bg-[#E2F3F0] border border-[#00a69c] rounded-lg" data-testid="class-suggestion">
                  <div className="text-xs uppercase font-bold text-[#00a69c] tracking-wide mb-1">💡 Suggested by RCA</div>
                  <div className="text-sm text-[#052A23]"><strong>{suggestion.value}</strong></div>
                  <div className="text-xs text-[#3B5A54] mt-1">Reason: {suggestion.reasons.join('; ')}</div>
                </div>
              )}
              <Field label="Incident Classification" required>
                <div className="grid grid-cols-1 gap-2">
                  {CLASSIFICATIONS.map(c => {
                    const selected = form.incident_classification === c.value;
                    return (
                      <button type="button" key={c.value}
                        onClick={() => { classManuallyChosenRef.current = true; set('incident_classification', c.value); }}
                        className={`p-3 text-left rounded-lg border-2 transition ${selected ? 'border-[#00a69c] bg-[#E2F3F0]' : 'border-[#D5E8E4] bg-white hover:border-[#00a69c]'}`}
                        data-testid={`f-class-${c.short.replace(/\s/g,'-')}`}>
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="font-semibold text-sm" style={{ color: c.color }}>{c.value}</span>
                          {selected && <span className="text-[10px] font-bold uppercase px-2 py-0.5 bg-[#00a69c] text-white rounded">Selected</span>}
                        </div>
                        <div className="text-xs text-[#3B5A54] mb-1">{c.desc}</div>
                        <div className="text-[11px] text-[#6D8E87]"><span className="font-semibold">Suggested action:</span> {c.action}</div>
                      </button>
                    );
                  })}
                </div>
              </Field>
            </div>
          )}

          {step === 9 && (
            <div className="space-y-3">
              <Field label="Action Type(s)" required>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {ACTION_TYPES.map(a => (
                    <label key={a} className={`flex items-center gap-2 p-2 border rounded-lg cursor-pointer ${form.action_types.includes(a)?'border-[#00a69c] bg-[#E2F3F0]':'border-[#D5E8E4]'}`} data-testid={`f-action-${a.replace(/\s/g,'-')}`}>
                      <input type="checkbox" checked={form.action_types.includes(a)} onChange={()=>toggleAction(a)}/>
                      <span className="text-sm">{a}</span>
                    </label>
                  ))}
                </div>
              </Field>

              {/* Per-action sub-forms */}
              {form.action_types.map(action => {
                const fields = ACTION_FIELDS[action] || [];
                if (fields.length === 0) return null;
                const details = (form.action_details || {})[action] || {};
                const safeId = action.replace(/\s/g, '-');
                return (
                  <div key={action} className="border-l-4 border-[#00a69c] bg-[#FAFFFE] p-3 rounded-r-lg" data-testid={`action-details-${safeId}`}>
                    <div className="text-sm font-bold text-[#00a69c] mb-2">{action} — Details</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {fields.map(f => (
                        <div key={f.key} className={f.type === 'textarea' ? 'sm:col-span-2' : ''}>
                          <label className="block text-xs font-medium text-[#3B5A54] mb-1">{f.label}</label>
                          {f.type === 'textarea' ? (
                            <textarea rows={2} value={details[f.key] || ''} placeholder={f.placeholder || ''}
                              onChange={e=>setActionDetail(action, f.key, e.target.value)}
                              className={inputCls} data-testid={`ad-${safeId}-${f.key}`}/>
                          ) : f.type === 'select' ? (
                            <select value={details[f.key] || ''} onChange={e=>setActionDetail(action, f.key, e.target.value)}
                              className={inputCls} data-testid={`ad-${safeId}-${f.key}`}>
                              <option value="">— Select —</option>
                              {(f.options || []).map(o => <option key={o}>{o}</option>)}
                            </select>
                          ) : (
                            <input type={f.type} value={details[f.key] || ''} placeholder={f.placeholder || ''}
                              onChange={e=>setActionDetail(action, f.key, e.target.value)}
                              className={inputCls} data-testid={`ad-${safeId}-${f.key}`}/>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              <Field label="Penalty Amount (₹)" hint="Optional. Leave blank if no monetary penalty."><input type="number" value={form.penalty_amount} onChange={e=>set('penalty_amount',e.target.value)} className={inputCls} data-testid="f-penalty"/></Field>
              <Field label="Additional Remarks"><textarea rows={2} value={form.additional_remarks} onChange={e=>set('additional_remarks',e.target.value)} className={inputCls} data-testid="f-remarks"/></Field>
            </div>
          )}

          {step === 10 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Supervisor Name" required><input value={form.supervisor_name} onChange={e=>set('supervisor_name',e.target.value)} className={inputCls} data-testid="f-sup"/></Field>
              <Field label="Plant Head Approval" required><input value={form.plant_head_approval} onChange={e=>set('plant_head_approval',e.target.value)} className={inputCls} data-testid="f-ph"/></Field>
              <Field label="HR Representative" required><input value={form.hr_representative} onChange={e=>set('hr_representative',e.target.value)} className={inputCls} data-testid="f-hr"/></Field>
            </div>
          )}
        </div>

        {error && <div className="p-3 mt-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="memo-error">{error}</div>}

        <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-[#F4F9F7]">
          <button type="button" onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1}
            className="px-4 py-2 border border-[#D5E8E4] rounded-lg disabled:opacity-50">Back</button>
          {step < 10 ? (
            <button type="button" onClick={() => setStep(s => Math.min(10, s + 1))}
              className="px-4 py-2 bg-[#00a69c] text-white rounded-lg font-medium hover:bg-[#00bfb3]" data-testid="memo-next-btn">
              Next: {SECTIONS.find(s => s.id === step + 1)?.title || ''}
            </button>
          ) : (
            <button type="button" onClick={submit} disabled={saving}
              className="px-6 py-2 bg-[#00a69c] text-white rounded-lg font-semibold hover:bg-[#00bfb3] disabled:opacity-50" data-testid="memo-submit-btn">
              {saving ? 'Saving...' : (existing ? 'Save Changes' : 'Issue Memo')}
            </button>
          )}
          <button type="button" onClick={onClose} className="ml-auto px-4 py-2 text-[#6D8E87] hover:text-[#052A23]">Cancel</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MemoModal;
